<template>
  <DxPopup
    :visible="getVisible"
    @hidden="onPopupHide"
    @shown="onShown"
    :drag-enabled="false"
    :hide-on-outside-click="false"
    :show-close-button="false"
    :show-title="true"
    :width="'85%'"
    :height="700"
    :title="getTitle"
    :wrapper-attr="{
      class: 'css-v2 gc-popup gc-popup-work-order-release-model',
    }"
    ref="gc-popup"
  >
    <DxToolbarItem
      v-for="(item, index) in getToolbarItems"
      :key="index"
      :widget="item.widget"
      :location="item.location"
      :visible="item.visible"
      :toolbar="item.position"
      :options="item.options"
    />

    <div class="css-v2 gc-popup-form">
      <gc-form
        :key="1"
        :form-options="{
          gcLabelLocation: 'left',
        }"
        ref="gc-header-form"
        :form-items="getHeaderFormItems"
        :is-normal="true"
        :data-model="getFormModel"
        :form-mode="popupMode"
        :from-from="'gc-header-form-d-a'"
        :form-height="35"
        v-if="loaded"
        :formContentReady="formContentReady"
      />

      <div class="data-grid" v-if="loaded">
        <dx-data-grid
          v-if="getVisible"
          :columnChooser="{
            enabled: false,
          }"
          :pager="{ visible: false }"
          :paging="{
            enabled: false,
          }"
          :columns="getColumns"
          :data-source="getDataSourceConfig"
          :row-alternation-enabled="true"
          :focusedRowEnabled="popupMode === 'create'"
          :height="getGridHeight"
          :width="'100%'"
          @editor-preparing="onEditorPreparing"
          @editing-start="onEditingStart"
          :ref="'gc-popup-release-model-grid'"
          @row-removed="onRowRemoved"
          @saved="onSaved"
          @cell-prepared="onCellPrepared"
          :repaint-changes-only="true"
          :two-way-binding-enabled="false"
        >
          <DxSearchPanel :visible="false" />
          <DxKeyboardNavigation
            :edit-on-key-press="true"
            :enter-key-action="'startEdit'"
            :enter-key-direction="'none'"
            :enabled="popupMode === 'create'"
          />

          <DxEditing
            :allow-deleting="popupMode === 'create'"
            :allow-updating="popupMode === 'create'"
            :mode="'cell'"
            :changes="changes"
            :confirm-delete="false"
          />

          <template #location-search>
            <div>
              <DxButton
                :text="''"
                icon="find"
                type="default"
                styling-mode="contained"
              />
            </div>
          </template>
        </dx-data-grid>
      </div>

      <gcPopupStorageLocationList
        :popupConfig="slPopupConfig"
        :add-selected-sl-lines="addSelectedStorageLocation"
        v-if="slPopupConfig.visible"
      />
    </div>
  </DxPopup>
</template>

<script>
import ArrayStore from "devextreme/data/array_store";
import { getActiveAccountPeriod } from "../../_helpers/default-value-rules/default-value-rules";
import { ProductionReleaseService } from "../../_services/api/production/production-release.service";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";
import SettingsService from "../../_services/api/settings/graycorp/settings.service";
import {
  backwardTabbing,
  forwardTabbing,
  onEnter,
} from "@/_helpers/ui/keyboard";
import GcPopupStorageLocationList from "../gc-popup-storage-location-list.vue";
import { ProductAttributeService } from "../../_services/api/settings/products/product-attribute.service";
import { WorkOrderService } from "../../_services/api/production/work-order.service";

import DataGridHelpers from "@/_helpers/data-grid/helpers";
const { countLineBreaks } = DataGridHelpers;
import { mapGetters } from "vuex";

let baseLine = {
  WorkOrderCode: null,
  ProductCode: null,
  ProductUM: null,
  Conversion: 0,
  StockUMConversion: 1,
  PalletNo: null,
  BatchNo: null,
  ProductionUMQty: 0,
  StockUMQty: 0,
  Comment: null,
  IsBatchProduct: "No",
  ProductType: null,
};

export default {
  components: {
    GcPopupStorageLocationList,
  },
  props: {
    popupConfig: {
      type: Object,
      default: () => {},
    },
    onHide: {
      type: Function,
      default: () => [],
    },
    showLoading: {
      type: Function,
      default: () => {},
    },
    popupMode: {
      type: String,
      default: "create",
    },
  },
  data() {
    return {
      model: {
        ReleaseDate: new Date(),
        BranchCode: undefined,
      },
      loaded: false,
      DateMin: undefined,
      DateMax: undefined,
      releaseDataSource: new ArrayStore({
        key: "ID",
        data: [],
      }),
      changes: [],

      ProductionReleasePalletVisibility: undefined,
      ProductionReleasePalletRequired: undefined,
      ProductionReleaseAutoGeneratedPallet: undefined,
      ProductionReleaseIsPalletUnique: undefined,
      IsWorkOrderEditable: true,
      FocusedOnHeaderForm: false,
      WorkOrderInstance: "Core",
      slPopupConfig: {
        visible: false,
        dataModel: {},
      },
      ignoreLocationChangeEvent: false,
      LineCellErrorVisible: false,
    };
  },
  async created() {
    await this.getProductionReleasesSettings();
    const datePeriod = await getActiveAccountPeriod();

    this.DateMin = datePeriod.DateMin;
    this.DateMax = datePeriod.DateMax;
    if (this.popupConfig.dataModel) {
      this.model = Object.assign(this.model, {
        ...this.popupConfig.dataModel,
      });

      if (this.popupConfig.dataModel.WorkOrderCode) {
        baseLine.WorkOrderCode = this.popupConfig.dataModel.WorkOrderCode;
        this.IsWorkOrderEditable = false;
      } else {
        baseLine.WorkOrderCode = null;
        baseLine.ProductCode = null;
        baseLine.BranchCode = null;
        baseLine.WorkOrderStatus = null;
        baseLine.StockUMConversion = null;
        baseLine.Conversion = 0;
        baseLine.IsBatchProduct = null;
        baseLine.StockUM = null;
        baseLine.ProductUM = null;
        baseLine.Conversion = null;
        this.IsWorkOrderEditable = true;
      }
    }

    this.releaseDataSource.insert(baseLine);

    if (this.model.ReleaseDate < this.DateMin) {
      this.model.ReleaseDate = new Date(this.DateMin);
    } else if (this.model.ReleaseDate > this.DateMax) {
      this.model.ReleaseDate = new Date(this.DateMax);
    }

    this.$nextTick(() => {
      this.loaded = true;
    });
  },
  methods: {
    displayServerErrorOnTemplate(err, msg) {
      this.$store.dispatch("ui/setServerError", {
        error: err,
        title: msg,
      });
    },
    async getProductionReleasesSettings() {
      await SettingsService.getBySettingByApplicationFeatureCode(
        "WorkOrder"
      ).then((res) => {
        res.data.value.forEach((element) => {
          if (element.SettingName == "ProductionReleasePalletVisibility") {
            this.ProductionReleasePalletVisibility = element.SettingValue;
          } else if (element.SettingName == "ProductionReleasePalletRequired") {
            this.ProductionReleasePalletRequired = element.SettingValue;
          } else if (
            element.SettingName == "ProductionReleaseAutoGeneratedPallet"
          ) {
            this.ProductionReleaseAutoGeneratedPallet = element.SettingValue;
          } else if (element.SettingName == "ProductionReleasePalletAi") {
            this.ProductionReleasePalletAi = element.SettingValue;
          } else if (element.SettingName == "ProductionReleaseIsPalletUnique") {
            this.ProductionReleaseIsPalletUnique = element.SettingValue;
          }
          // else if (element.SettingName == "AlmondcoFGAttribute") {
          //   console.log(
          //     "AlmondcoFGAttribute setting found:",
          //     element.SettingValue
          //   );
          // } else if (element.SettingName == "AlmondcoFGAttributeValue") {
          //   console.log(
          //     "AlmondcoFGAttributeValue setting found:",
          //     element.SettingValue
          //   );
          // } else if (element.SettingName == "AlmondcoWIPAttributeValue") {
          //   console.log(
          //     "AlmondcoWIPAttributeValue setting found:",
          //     element.SettingValue
          //   );
          // }
          else if (element.SettingName == "WorkOrderInstance") {
            this.WorkOrderInstance = element.SettingValue;
          }
        });
      });
    },
    async onPopupHide() {
      this.onHide();
      this.$store.dispatch("hotKey/setHotKeyPopupMode", false);
    },
    onShown() {
      this.$store.dispatch("hotKey/setHotKeyPopupMode", true);
    },

    async onPopupClose() {
      const confirmLeaving = await this.$confirm(
        "Are you sure you want to discard this production release?",
        "Unsaved changes"
      );
      if (confirmLeaving) {
        this.onHide();
      }
    },
    onEditorPreparing(e) {
      if (e.dataField && e.dataField == "WorkOrderCode") {
        e.editorName = "dxSelectBox";
      }

      if (e.dataField && e.dataField == "Comment") {
        e.editorName = "dxTextArea";
      }

      if (e.parentType !== "searchPanel") {
        if (e.editorOptions && e.editorOptions.onFocusOut) {
          let func = e.editorOptions.onFocusOut;
          e.editorOptions.onFocusOut = (event) => {
            func(event, {
              column: {
                dataField: e.dataField,
              },
              data: e.row.data,
              rowIndex: e.row.rowIndex,
            });
          };
        }

        if (e.editorOptions && e.editorOptions.onFocusIn) {
          let func = e.editorOptions.onFocusIn;
          e.editorOptions.onFocusIn = (event) => {
            func(event, {
              column: {
                dataField: e.dataField,
              },
              data: e.row.data,
              rowIndex: e.row.rowIndex,
            });
          };
        }

        if (e.editorOptions && e.editorOptions.onValueChanged) {
          let func = e.editorOptions.onValueChanged;
          e.editorOptions.onValueChanged = (event) => {
            func(event, {
              column: {
                dataField: e.dataField,
              },
              data: e.row.data,
              rowIndex: e.row.rowIndex,
            });
          };
        }

        if (e.editorOptions && e.editorOptions.onItemClick) {
          let func = e.editorOptions.onItemClick;
          e.editorOptions.onItemClick = (event) => {
            func(event, {
              column: {
                dataField: e.dataField,
              },
              data: e.row.data,
              rowIndex: e.row.rowIndex,
            });
          };
        }

        if (e.editorOptions && e.editorOptions.onKeyDown) {
          let func = e.editorOptions.onKeyDown;
          e.editorOptions.onKeyDown = (event) => {
            func(event, {
              column: {
                dataField: e.dataField,
              },
              data: e.row.data,
              rowIndex: e.row.rowIndex,
            });
          };
        }

        if (e.editorOptions && e.editorOptions.onOpened) {
          let func = e.editorOptions.onOpened;
          e.editorOptions.onOpened = (event) => {
            func(event, {
              column: {
                dataField: e.dataField,
              },
              data: e.row.data,
              rowIndex: e.row.rowIndex,
            });
          };
        }

        if (e.dataField === "StorageLocationCode") {
          if (
            e.editorOptions &&
            e.editorOptions.buttons &&
            e.editorOptions.buttons.length > 0
          ) {
            const gridInstance = this.dataGrid; // assuming your DxDataGrid has ref="grid"

            e.editorOptions.buttons.forEach((button) => {
              if (button.name === "search") {
                const originalClick = button.options.onClick;

                button.options.onClick = (event) => {
                  // Traverse DOM to find the row element
                  const cell = event.event.target.closest(
                    ".dx-editor-cell, .dx-cell"
                  );
                  const rowElement = cell.closest(".dx-row");

                  let rowIndex = -1;
                  let rowData = null;

                  if (rowElement && gridInstance) {
                    const visibleRows = gridInstance.getVisibleRows();
                    const rowElements = gridInstance
                      .getScrollable()
                      .content()
                      .querySelectorAll(".dx-row");

                    rowElements.forEach((rowEl, i) => {
                      if (rowEl === rowElement) {
                        rowIndex = i;
                        rowData = visibleRows[i].data;
                      }
                    });
                  }

                  originalClick(event, {
                    column: {
                      dataField: e.dataField,
                    },
                    data: rowData,
                    rowIndex: rowIndex,
                  });
                };
              }
            });
          }
        }
      }
    },
    onEditingStart(e) {
      const rowData = e.data;
      if (rowData && this.popupMode === "create") {
        if (e.column.dataField === "ProductCode") {
          if (!rowData.WorkOrderCode) {
            e.cancel = true;
          }
        }

        if (e.column.dataField === "ProductUM") {
          if (!rowData.ProductCode) {
            e.cancel = true;
          }
        }

        if (e.column.dataField === "BatchNo") {
          if (
            !rowData.ProductUM ||
            rowData.IsBatchProduct.toLowerCase() != "manual"
          ) {
            e.cancel = true;
          }
        }

        if (
          e.column.dataField === "PalletNo" ||
          e.column.dataField === "CardNo" ||
          e.column.dataField === "ProductionUMQty" ||
          e.column.dataField === "StockUMQty" ||
          e.column.dataField === "StorageLocationCode" ||
          e.column.dataField === "Comment"
        ) {
          if (!rowData.ProductUM) {
            e.cancel = true;
          }

          if (
            (e.column.dataField === "CardNo" ||
              e.column.dataField === "StorageLocationCode") &&
            this.WorkOrderInstance !== "Core" &&
            rowData.ProductType === "FG"
          ) {
            e.cancel = true;
          }
        }
      }
    },
    onCellPrepared(e) {
      if (e.rowType !== "data") return;

      const { data, column, cellElement } = e;
      const field = column.dataField;

      const requiredFields = {
        WorkOrderCode: !data.WorkOrderCode,
        ProductCode: !data.ProductCode,
        ProductUM: !data.ProductUM,
        PalletNo:
          this.ProductionReleasePalletRequired === "Y" &&
          this.ProductionReleasePalletVisibility === "Y" &&
          !data.PalletNo,
        BatchNo:
          data.IsBatchProduct &&
          data.IsBatchProduct.toLowerCase() === "manual" &&
          !data.BatchNo,
      };

      if (requiredFields[field]) {
        cellElement.classList.add("required-field");
      } else {
        cellElement.classList.remove("required-field");
      }
    },
    getProductDataSource(workOderCode) {
      if (this.WorkOrderInstance !== "Core") {
        return ProductionReleaseService.dataSourceConfig(
          "api/AlmondcoReleaseSourceProduct?WorkOrderCode=" +
            parseInt(workOderCode),
          {
            // filter: [["IsBatchProduct", "=", "manual"]],
            // key: "RecordId",
            // keyType: {
            //   RecordId: "Guid",
            // },
            paginate: true,
          }
        );
      } else {
        return ProductionReleaseService.dataSourceConfig(
          "api/ProductionReleaseProductList",
          {
            filter: ["WorkOrderCode", "=", parseInt(workOderCode)],
            key: "RecordId",
            keyType: {
              RecordId: "Guid",
            },
            paginate: true,
          }
        );
      }
    },
    getUMDataSource(rowInfo) {
      const data = rowInfo.data;
      return ProductionReleaseService.dataSourceConfig(
        "api/ProductUnitofMeasure",
        {
          filter: [
            ["ProductCode", "=", `${data.ProductCode}`],
            "and",
            ["BranchCode", "=", `${data.BranchCode}`],
            "and",
            ["IsProductionUM", "=", "Y"],
          ],
          key: "RecordId",
          keyType: {
            RecordId: "Guid",
          },
          paginate: true,
        }
      );
    },
    async onWorkOrderValueChanged(e, rowInfo) {
      const selectedValue = e.value;
      const component = e.component; // the dxSelectBox instance
      const selectedItem = component
        .option("items")
        .find((item) => item.Code === selectedValue);

      let newData = {};
      const currentRowData = rowInfo.data;

      let confirmChanging = true;
      if (currentRowData.ProductCode) {
        confirmChanging = await this.$confirm(
          "Changing the Work Order will clear all fields for this entry. Do you wish to proceed?",
          "Change Work Order"
        );

        if (confirmChanging) {
          newData = {
            ...baseLine,
            ...{
              WorkOrderCode: e.value,
              WorkOrderStatus: selectedItem.Status,
              BranchCode: selectedItem.BranchCode,
            },
          };
          this.releaseDataSource.update(currentRowData.ID, newData);

          await this.$nextTick();

          const rowIndex = this.dataGrid.getRowIndexByKey(currentRowData.ID);
          setTimeout(() => {
            this.focusOnEditableCell(rowIndex, "ProductCode");
          }, 100);
        } else {
          this.dataGrid.cellValue(
            rowInfo.rowIndex,
            rowInfo.column.dataField,
            currentRowData.WorkOrderCode
          );
        }
      } else {
        this.$nextTick(() => {
          if (selectedItem) {
            // If no product is selected, we can update the Work Order Code without confirmation
            this.releaseDataSource.update(rowInfo.data.ID, {
              WorkOrderCode: e.value,
              WorkOrderStatus: selectedItem.Status,
              BranchCode: selectedItem.BranchCode,
            });
          } else {
            this.releaseDataSource.update(rowInfo.data.ID, {
              WorkOrderCode: e.value,
            });
          }
          this.dataGrid.cellValue(
            rowInfo.rowIndex,
            rowInfo.column.dataField,
            e.value
          );
        });
      }
    },

    async onProductValueChanged(e, rowInfo) {
      const selectedValue = e.value;
      const component = e.component; // the dxSelectBox instance
      const selectedItem = component
        .option("items")
        .find((item) => item.ProductCode === selectedValue);

      if (selectedItem) {
        let dataSet = {
          ProductCode: e.value,
          BranchCode: selectedItem.BranchCode,
          StockUMConversion: selectedItem.StockUMConversion,
          IsBatchProduct: selectedItem.IsBatchProduct,
          StockUM: selectedItem.StockUM,
          PalletNo: null,
          BatchNo: null,
          ProductionUMQty: 0,
          StockUMQty: 0,
          Comment: null,
        };
        if (this.WorkOrderInstance !== "Core") {
          dataSet.ProductType = selectedItem.ProductType;
        }
        this.releaseDataSource
          .update(rowInfo.data.ID, dataSet)
          .then(async (res) => {
            await this.getProductPrimaryProductionUM(res);
            this.runNextPalletNo(res);
          });
      } else {
        this.releaseDataSource.update(rowInfo.data.ID, {
          ProductCode: e.value,
          BranchCode: null,
          StockUMConversion: null,
          Conversion: 0,
          IsBatchProduct: "No",
          ProductUM: null,
          StockUM: null,
          PalletNo: null,
          BatchNo: null,
          ProductionUMQty: 0,
          StockUMQty: 0,
          Comment: null,
        });
      }

      this.$nextTick(async () => {
        await this.addNextLineOnProductSelect(rowInfo.data);

        this.$nextTick(() => {
          var dataGrid = this.$refs["gc-popup-release-model-grid"].instance;
          dataGrid.saveEditData();
          dataGrid.refresh().then(() => {
            this.focusOnEditableCell(rowInfo.rowIndex, "ProductUM");
          });
        });
      });
    },

    async addNextLineOnProductSelect(rowInfo, refresh = false) {
      const items = await this.releaseDataSource.load();
      const rowIndex = this.dataGrid.getRowIndexByKey(rowInfo.ID);
      if (rowIndex + 1 === items.length) {
        let newBaseLine = { ...baseLine };
        if (rowInfo.StorageLocationRecordId) {
          newBaseLine = {
            ...newBaseLine,
            StorageLocationRecordId: rowInfo.StorageLocationRecordId,
            StorageLocationCode: rowInfo.StorageLocationCode,
            LocationDescription: rowInfo.LocationDescription,
          };
        }
        this.releaseDataSource.insert(newBaseLine);
      }

      if (refresh) {
        this.$nextTick(() => {
          var dataGrid = this.$refs["gc-popup-release-model-grid"].instance;
          dataGrid.saveEditData();
          dataGrid.refresh();
        });
      }
    },
    async onUMValueChanged(e, rowInfo) {
      const selectedValue = e.value;
      const component = e.component; // the dxSelectBox instance
      const selectedItem = component
        .option("items")
        .find((item) => item.UnitOfMeasureCode === selectedValue);

      if (selectedItem) {
        this.releaseDataSource.update(rowInfo.data.ID, {
          ProductUM: e.value,
          Conversion: selectedItem.ConversionFactor,
          StockUMQty: (
            rowInfo.data.ProductionUMQty *
            (selectedItem.ConversionFactor / rowInfo.data.StockUMConversion)
          ).roundNumber(4),
        });
      }

      this.$nextTick(() => {
        var dataGrid = this.$refs["gc-popup-release-model-grid"].instance;
        dataGrid.saveEditData();
        let dataField = "PalletNo";
        if (this.ProductionReleasePalletVisibility !== "Y") {
          if (rowInfo.data.IsBatchProduct.toLowerCase() !== "manual") {
            dataField = "ProductionUMQty";
          } else {
            dataField = "BatchNo";
          }
        }

        this.$nextTick(() => {
          setTimeout(() => {
            this.focusOnEditableCell(rowInfo.rowIndex, dataField);
          }, 100);
        });
      });
    },
    async getProductPrimaryProductionUM(data) {
      try {
        const response = await ProductionReleaseService.getPrimaryProductionUM({
          filter: `ProductCode eq '${data.ProductCode}' and BranchCode eq '${data.BranchCode}' and IsProductionUM eq 'Y' and IsPrimaryProductionUM eq 'Y'`,
        });
        if (response.data.value.length > 0) {
          this.releaseDataSource
            .update(data.ID, {
              ProductUM: response.data.value[0].UnitOfMeasureCode,
              Conversion: response.data.value[0].ConversionFactor,
            })
            .then(() => {
              this.$nextTick(() => {
                var dataGrid =
                  this.$refs["gc-popup-release-model-grid"].instance;
                dataGrid.saveEditData();
                dataGrid.refresh();
              });
            });
        } else {
          this.releaseDataSource.update(data.ID, {
            ProductUM: null,
            Conversion: 0,
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Product Primary Production UM : ", error);
      }
    },
    runNextPalletNo(rowInfo) {
      if (
        this.ProductionReleasePalletVisibility === "Y" &&
        this.ProductionReleaseAutoGeneratedPallet === "N"
      ) {
        return;
      }

      if (rowInfo.IsBatchProduct.toLowerCase() === "no") {
        this.getNextPalletNo(rowInfo);
      } else if (rowInfo.IsBatchProduct.toLowerCase() !== "manual") {
        this.getNextPalletNo(rowInfo);
      } else if (
        rowInfo.IsBatchProduct.toLowerCase() === "manual" &&
        rowInfo.BatchNo
      ) {
        this.getNextPalletNo(rowInfo);
      }
    },
    async getNextPalletNo(rowInfo) {
      ProductionReleaseService.getNextPalleteNo(
        rowInfo.WorkOrderCode,
        rowInfo.ProductCode,
        rowInfo.BatchNo
      )
        .then(async (res) => {
          if (res.data) {
            let PalletNo = res.data;
            const items = await this.releaseDataSource.load({
              filter: [
                ["WorkOrderCode", "=", rowInfo.WorkOrderCode],
                "and",
                ["ProductCode", "=", rowInfo.ProductCode],
                "and",
                ["BatchNo", "=", rowInfo.BatchNo],
                "and",
                ["ID", "<>", rowInfo.ID],
              ],
            });

            if (items.length > 0) {
              const maxPalletNo = Math.max(
                ...items.map((item) => Number(item.PalletNo) || 0)
              );

              if (maxPalletNo >= PalletNo) {
                PalletNo = maxPalletNo + 1;
              }
            }

            this.releaseDataSource
              .update(rowInfo.ID, {
                PalletNo: PalletNo,
              })
              .then(() => {
                this.$nextTick(() => {
                  var dataGrid =
                    this.$refs["gc-popup-release-model-grid"].instance;
                  dataGrid.saveEditData();
                  dataGrid.refresh();
                });
              });
          }
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error("Error getting next pallet number: ", error);
        });
    },
    focusOnEditableCell(rowIndex, col) {
      const column = col ? col : "ProductCode";
      const dataGrid = this.dataGrid;
      const cellElement = dataGrid.getCellElement(rowIndex, column);
      dataGrid.focus(cellElement);
      setTimeout(() => {
        dataGrid.editCell(rowIndex, column);
      }, 10);
    },
    onUMForwardTabbing(e, lineData) {
      e.event.preventDefault();
      if (lineData.data.IsBatchProduct.toLowerCase() !== "manual") {
        if (
          this.WorkOrderInstance !== "Core" &&
          lineData.data.ProductType !== "FG"
        ) {
          this.focusOnEditableCell(lineData.rowIndex, "CardNo");
        } else if (this.ProductionReleasePalletVisibility === "Y") {
          this.focusOnEditableCell(lineData.rowIndex, "PalletNo");
        } else {
          this.focusOnEditableCell(lineData.rowIndex, "ProductionUMQty");
        }
      } else {
        this.focusOnEditableCell(lineData.rowIndex, "BatchNo");
      }
    },
    setFocusOnHeaderForm() {
      const form = this.$refs["gc-header-form"].$refs["gc-form-comp"].instance;
      if (form) {
        form.getEditor("BranchCode").focus();
        this.removeGridFocusRow();
      }
    },
    /**
     * Remove Focus on DataGrid
     */
    removeGridFocusRow() {
      if (this.dataGrid) {
        this.dataGrid.option("focusedRowIndex", -1);
      }
    },
    formContentReady(e) {
      if (e && this.loaded && !this.FocusedOnHeaderForm) {
        this.FocusedOnHeaderForm = true;
        setTimeout(() => {
          var headerToolBarForm = e.component;
          if (headerToolBarForm && headerToolBarForm.getEditor("ReleaseDate")) {
            headerToolBarForm.getEditor("ReleaseDate").instance().focus();
          }
        }, 500);
      }
    },
    async onRowRemoved(e) {
      const dataField = this.IsWorkOrderEditable
        ? "WorkOrderCode"
        : "ProductCode";
      const items = await this.releaseDataSource.load();
      if (items.length === 0) {
        this.releaseDataSource.insert(baseLine);
        e.component.refresh();
        await this.$nextTick();

        setTimeout(() => {
          this.focusOnEditableCell(0, dataField);
        }, 100);
      } else {
        if (e.data && (!e.data.WorkOrderCode || !e.data.ProductCode)) {
          this.releaseDataSource.insert(baseLine);
          e.component.refresh();
          await this.$nextTick();

          setTimeout(() => {
            this.focusOnEditableCell(items.length, dataField);
          }, 100);
        }
      }
    },
    async addCopyReleaseLine(e) {
      if (e.event) {
        const data = e.row.data;

        if (!data.WorkOrderCode || !data.ProductCode) {
          await this.displayLineCellError(
            "Please select a work order and product before copying.",
            "Validation Error"
          );
          if (!data.WorkOrderCode) {
            this.focusOnEditableCell(e.row.rowIndex, "WorkOrderCode");
          } else {
            this.focusOnEditableCell(e.row.rowIndex, "ProductCode");
          }
          return;
        } else if (
          !data.PalletNo &&
          this.ProductionReleasePalletRequired === "Y" &&
          this.ProductionReleasePalletVisibility === "Y"
        ) {
          await this.displayLineCellError(
            "Please enter a pallet number before copying.",
            "Validation Error"
          );
          this.focusOnEditableCell(e.row.rowIndex, "PalletNo");
          return;
        } else if (
          !data.BatchNo &&
          data.IsBatchProduct.toLowerCase() === "manual"
        ) {
          await this.displayLineCellError(
            "Please enter a batch number before copying.",
            "Validation Error"
          );
          this.focusOnEditableCell(e.row.rowIndex, "BatchNo");
          return;
        } else if (data.ProductionUMQty <= 0 || data.StockUMQty <= 0) {
          await this.displayLineCellError(
            "Please enter valid quantities before copying.",
            "Validation Error"
          );
          if (data.ProductionUMQty <= 0) {
            this.focusOnEditableCell(e.row.rowIndex, "ProductionUMQty");
          } else {
            this.focusOnEditableCell(e.row.rowIndex, "StockUMQty");
          }
          return;
        } else {
          const newLine = { ...data };

          const isVisible = this.ProductionReleasePalletVisibility === "Y";
          const isUnique = this.ProductionReleaseIsPalletUnique === "Y";
          const isAutoGenerated =
            this.ProductionReleaseAutoGeneratedPallet === "Y";
          const isRequired = this.ProductionReleasePalletRequired === "Y";

          if (isVisible && isUnique && isAutoGenerated && isRequired) {
            const items = await this.releaseDataSource.load({
              filter: [
                ["WorkOrderCode", "=", newLine.WorkOrderCode],
                "and",
                ["ProductCode", "=", newLine.ProductCode],
                "and",
                ["BatchNo", "=", newLine.BatchNo],
              ],
            });

            const maxPalletNo = Math.max(
              ...items.map((item) => Number(item.PalletNo) || 0)
            );

            newLine.PalletNo = maxPalletNo + 1;
          } else if (isVisible && isUnique && !isAutoGenerated) {
            // Required or not, clear it in both cases
            newLine.PalletNo = null;
          } else {
            // Covers not visible or any other unspecified combination
            newLine.PalletNo = null;
          }

          if (newLine.ProductType === "WIP") {
            newLine.CardNo = null; // Clear CardNo for WIP products
          }

          delete newLine.ID; // Remove ID to create a new record

          this.changes = [
            {
              type: "insert",
              data: newLine,
              insertAfterKey: e.row.key,
            },
          ];

          const dataGrid = this.dataGrid;
          dataGrid.clearSelection();
          dataGrid.option("focusedRowIndex", -1);

          this.$nextTick(() => {
            dataGrid.saveEditData();
            dataGrid.refresh(true);
          });
        }
      }
    },
    onSaved(e) {
      if (e.changes && e.changes.length > 0) {
        const change = e.changes[0];
        if (change.insertAfterKey !== undefined) {
          const element = change.data;
          let index = -1;

          index = this.releaseDataSource._array.findIndex(
            (x) => x.ID === change.insertAfterKey
          );
          this.releaseDataSource._array.splice(
            this.releaseDataSource._array.length - 1,
            1
          );
          this.releaseDataSource._array.splice(index + 1, 0, element);

          if (index !== -1)
            setTimeout(() => {
              this.focusOnEditableCell(index + 1, "WorkOrderCode");
            }, 100);
        }
      }
    },

    // Method to save the production release
    async onSaveProductionRelease() {
      const filter = this.IsWorkOrderEditable
        ? [["WorkOrderCode", "<>", null], "and", ["ProductionUMQty", "<>", 0]]
        : [["ProductCode", "<>", null], "and", ["ProductionUMQty", "<>", 0]];
      const items = await this.releaseDataSource.load({
        filter: filter,
      });

      if (items.length === 0) {
        await this.displayLineCellError(
          "Please add at least one production release line before saving.",
          "Validation Error"
        );
        return;
      } else {
        const noProduct = items.find(
          (item) => !item.ProductCode || !item.ProductUM
        );
        if (noProduct) {
          await this.displayLineCellError(
            "Please select a product and unit of measure for all production release lines before saving.",
            "Validation Error"
          );

          let dataField = "ProductCode";
          if (noProduct.ProductCode && !noProduct.ProductUM) {
            dataField = "ProductUM";
          }

          const rowIndex = this.dataGrid.getRowIndexByKey(noProduct.ID);
          this.focusOnEditableCell(rowIndex, dataField);
          return;
        }

        if (
          this.ProductionReleasePalletRequired === "Y" &&
          this.ProductionReleasePalletVisibility === "Y"
        ) {
          const noPallet = items.find((item) => !item.PalletNo && !item.CardNo);
          if (noPallet) {
            await this.displayLineCellError(
              "Please enter a pallet number for all production release lines before saving.",
              "Validation Error"
            );

            const rowIndex = this.dataGrid.getRowIndexByKey(noPallet.ID);
            this.focusOnEditableCell(rowIndex, "PalletNo");
            return;
          }
        }

        const noBatch = items.find(
          (item) =>
            item.IsBatchProduct.toLowerCase() === "manual" && !item.BatchNo
        );

        if (noBatch) {
          await this.displayLineCellError(
            "Please enter a batch number for all manual batch products before saving.",
            "Validation Error"
          );

          const rowIndex = this.dataGrid.getRowIndexByKey(noBatch.ID);
          this.focusOnEditableCell(rowIndex, "BatchNo");
          return;
        }

        const noQty = items.find(
          (item) => item.ProductionUMQty <= 0 || item.StockUMQty <= 0
        );

        if (noQty) {
          await this.displayLineCellError(
            "Please enter valid quantities for all production release lines before saving.",
            "Validation Error"
          );

          const rowIndex = this.dataGrid.getRowIndexByKey(noQty.ID);
          if (noQty.ProductionUMQty <= 0) {
            this.focusOnEditableCell(rowIndex, "ProductionUMQty");
          } else {
            this.focusOnEditableCell(rowIndex, "StockUMQty");
          }
          return;
        }

        this.showLoading(true);

        const payload =
          this.WorkOrderInstance === "Core"
            ? this.getCoreVersionPayload(items)
            : this.getAlmondcoVersionPayload(items);

        try {
          const response = await ProductionReleaseService.saveProductionRelease(
            this.WorkOrderInstance === "Core"
              ? "Save_ProductionRelease"
              : "Save_AlmondcoProductionRelease",
            payload
          );

          if (response) {
            this.$notify(
              `Production releases have been successfully saved.`,
              "success"
            );
          }

          this.$nextTick(() => {
            this.onHide();
            this.$emit("onRefreshDataList");
            this.$emit("onRefreshWorkOrderHeader");
          });
        } catch (error) {
          if (error && error.data && error.data.Errors) {
            if (
              Array.isArray(error.data.Errors) &&
              error.data.Errors[0].MessageType != undefined
            ) {
              this.displayLineCellError(
                error.data.Errors[0].Message,
                "Server Error"
              );
            } else {
              this.displayServerErrorOnTemplate(
                error,
                "Error saving production release"
              );
            }
          } else {
            this.displayLineCellError(
              `Unable to save production release. Please try again later.`,
              "Error"
            );
          }
        } finally {
          this.showLoading(false);
        }
      }
    },

    getCoreVersionPayload(items) {
      let payload = {
        ReleaseCode: 0,
        DateIn: this.model.ReleaseDate.toSaveDate().replace("Z", ""),
        BranchCode: this.model.BranchCode,
        ProductionReleaseLines: [],
      };

      for (const item of items) {
        const releaseLine = {
          Comments: item.Comment,
          WorkOrderCode: item.WorkOrderCode,
          WorkOrderStatus: item.WorkOrderStatus,
          ProductCode: item.ProductCode,
          ItemQty: item.StockUMQty,
          ProductionReleaseLineBatches: item.BatchNo
            ? [
                {
                  BatchNo: item.BatchNo,
                  BatchQty: item.StockUMQty,
                },
              ]
            : [],
          PalletNo: item.PalletNo,
          ProductionUM: item.ProductUM,
          ProductionUMReleaseQty: item.ProductionUMQty,
          ProductionUMConversion: item.Conversion,
        };

        payload.ProductionReleaseLines.push(releaseLine);
      }

      return payload;
    },

    getAlmondcoVersionPayload(items) {
      let payload = {
        ReleaseCode: 0,
        DateIn: this.model.ReleaseDate.toSaveDate().replace("Z", ""),
        BranchCode: this.model.BranchCode,
        ProductionReleaseLines: [],
      };

      for (const item of items) {
        const releaseLine = {
          WorkOrderCode: item.WorkOrderCode,
          WorkOrderStatus: item.WorkOrderStatus,
          ProductCode: item.ProductCode,
          ItemQty: item.StockUMQty,
          PalletNo: item.PalletNo,
          CardNo: item.CardNo,
          ProductionUM: item.ProductUM,
          ProductionUMConversion: item.Conversion,
          ProductionUMReleaseQty: item.ProductionUMQty,
          StorageLocationRecordId: item.StorageLocationRecordId,
          // BinLocationCode: item.BinLocationCode,
          Comments: item.Comment,
          ProductionReleaseLineBatches: item.BatchNo
            ? [
                {
                  BatchNo: item.BatchNo,
                  BatchQty: item.StockUMQty,
                },
              ]
            : [],
        };

        if (item.CardNo === null || item.CardNo === undefined) {
          delete releaseLine.CardNo;
        }

        payload.ProductionReleaseLines.push(releaseLine);
      }

      return payload;
    },

    async addSelectedStorageLocation(
      selectedStorageLocationArray,
      rowInfo = null
    ) {
      if (selectedStorageLocationArray.length > 0) {
        this.ignoreLocationChangeEvent = true;
        this.releaseDataSource
          .update(
            rowInfo ? rowInfo.data.ID : this.slPopupConfig.rowInfo.data.ID,
            {
              StorageLocationCode: selectedStorageLocationArray[0].Code,
              StorageLocationRecordId: selectedStorageLocationArray[0].RecordId,
              LocationDescription: selectedStorageLocationArray[0].Name,
            }
          )
          .then(() => {
            this.$nextTick(async () => {
              const loadItems = await this.releaseDataSource.load({
                filter: [
                  ["StorageLocationCode", "=", null],
                  "or",
                  ["StorageLocationCode", "=", undefined],
                  "or",
                  ["StorageLocationCode", "=", ""],
                  "or",
                  ["StorageLocationRecordId", "=", null],
                  "or",
                  ["StorageLocationRecordId", "=", undefined],
                  "or",
                  ["StorageLocationRecordId", "=", ""],
                ],
              });

              for (const item of loadItems) {
                if (
                  item.ID !==
                  (rowInfo
                    ? rowInfo.data.ID
                    : this.slPopupConfig.rowInfo.data.ID)
                ) {
                  this.releaseDataSource.update(item.ID, {
                    StorageLocationCode: selectedStorageLocationArray[0].Code,
                    StorageLocationRecordId:
                      selectedStorageLocationArray[0].RecordId,
                    LocationDescription: selectedStorageLocationArray[0].Name,
                  });
                }
              }

              if (loadItems.length > 0) {
                this.dataGrid.refresh(true);
              }
            });
          });

        this.$nextTick(() => {
          const dataGrid = this.dataGrid;
          dataGrid.saveEditData();
          setTimeout(() => {
            this.focusOnEditableCell(
              rowInfo ? rowInfo.rowIndex : this.slPopupConfig.rowInfo.rowIndex,
              "Comment"
            );
          }, 100);
        });
      }
    },
    async OnNewLocationValueChanged(e, rowInfo) {
      if (!e.value) {
        this.dataGrid.cellValue(
          rowInfo.rowIndex,
          rowInfo.column.dataField,
          e.value
        );

        this.releaseDataSource.update(rowInfo.data.ID, {
          StorageLocationRecordId: null,
          LocationDescription: null,
        });
        return;
      }
      if (this.ignoreLocationChangeEvent) {
        this.ignoreLocationChangeEvent = false;
        return;
      }

      let result = await ProductAttributeService.checkStorageLocationExists(
        e.value
      );

      if (result && result.data.value.length == 0) {
        await this.displayLineCellError(
          `${e.value} is not a valid location.`,
          "Not a valid location"
        );
        setTimeout(() => {
          this.focusOnEditableCell(rowInfo.rowIndex, "StorageLocationCode");
        }, 100);
      } else {
        this.addSelectedStorageLocation(result.data.value, rowInfo);
      }
    },

    onLocationSearchClick(lineData) {
      this.slPopupConfig.rowInfo = lineData;
      this.slPopupConfig.visible = true;
    },
    onStorageLocationCodeFocusIn() {
      this.ignoreLocationChangeEvent = false;
    },
    commentFieldHeightUpdate(e) {
      let instance = e.component;
      let count = countLineBreaks(instance.instance().option().value);
      // Adjust the height of the comment field based on the number of line breaks
      // If there are less than 7 line breaks, set the height to count * 16
      // If there are 7 or more line breaks, set the height to 250px
      // This is to ensure that the comment field does not grow too large
      // and remains user-friendly.
      // The height is set to 16px per line break, with a maximum height of 250px
      if (count < 7) {
        if (count > 2) {
          instance.instance().beginUpdate();
          instance.instance().option("height", count * 16);
          instance.instance().endUpdate();
          this.dataGrid.updateDimensions();
        }
      } else {
        instance.instance().beginUpdate();
        instance.instance().option("height", 250);
        instance.instance().endUpdate();
        this.dataGrid.updateDimensions();
      }
    },

    updateLineErrorVisibility() {
      this.LineCellErrorVisible = !this.LineCellErrorVisible;
    },
    async displayLineCellError(errorMessage, errorTitle = "Validation Error") {
      if (!this.LineCellErrorVisible) {
        this.updateLineErrorVisibility();
        await this.$alert(errorMessage, errorTitle);
        this.updateLineErrorVisibility();
      }
    },
  },
  computed: {
    ...mapGetters({ getHotKeySave: "hotKey/getHotKeySave" }),
    getTitle() {
      if (this.popupMode === "create") {
        return "Create Production Release";
      } else {
        return "Release #" + this.popupConfig.dataModel.ProductionReleaseCode;
      }
    },
    getHeaderFormItems() {
      return [
        {
          itemType: "group",
          caption: "",
          colCount: 3,
          items: [
            {
              label: {
                text: "Release Date",
              },
              dataField: "ReleaseDate",
              editorType: "dxDateBox",
              editorOptions: {
                displayFormat: "dd MMM yyyy",
                width: 150,
                min: this.DateMin,
                max: this.DateMax,
                onFocusIn: (e) => {
                  e.event.target.select();
                },
              },
            },
            {
              screenField: "Branch",
              dataField: "BranchCode",
              label: {
                text: "Branch",
              },
              cssClass: "branch-field",
              editorOptions: {
                dropDownOptions: {
                  width: 200,
                },
                onFocusIn: (e) => {
                  e.event.target.select();
                },
                onKeyDown: (e) => {
                  if (forwardTabbing(e) && this.popupMode === "create") {
                    e.event.preventDefault();
                    const dataGrid = this.dataGrid;
                    if (this.releaseDataSource._array.length > 0) {
                      let rowIndex = 0;
                      let dataField = this.IsWorkOrderEditable
                        ? "WorkOrderCode"
                        : "ProductCode";
                      const cellElement = dataGrid.getCellElement(
                        rowIndex,
                        dataField
                      );
                      dataGrid.focus(cellElement);
                      dataGrid.editCell(rowIndex, dataField);
                    }
                  }
                },
              },
            },
          ],
        },
      ];
    },
    getFormModel() {
      return this.model;
    },
    getVisible() {
      if (this.popupConfig.isPopupShow) return this.popupConfig.isPopupShow;
      else return false;
    },
    getToolbarItems() {
      if (this.popupMode === "create") {
        return [
          {
            widget: "dxButton",
            location: "after",
            toolbar: "top",
            visible: true,
            options: {
              text: "Save",
              stylingMode: "contained",
              type: "default",
              onClick: this.onSaveProductionRelease,
            },
          },
          {
            widget: "dxButton",
            location: "after",
            toolbar: "top",
            visible: true,
            options: {
              text: "",
              icon: "close",
              stylingMode: "text",
              type: "default",
              onClick: this.onPopupClose,
            },
          },
        ];
      } else {
        return [
          {
            widget: "dxButton",
            location: "after",
            toolbar: "top",
            visible: true,
            options: {
              text: "",
              icon: "close",
              stylingMode: "text",
              type: "default",
              onClick: this.onPopupHide,
            },
          },
        ];
      }
    },

    getColumns() {
      return [
        {
          caption: "Work Order",
          dataType: "number",
          dataField: "WorkOrderCode",
          cssClass: "edit-cell-textbox-field",
          editorType: "dxSelectBox",
          editorOptions: {
            dataSource: ProductionReleaseService.dataSourceConfig(
              "api/WorkOrderList",
              {
                filter: [
                  ["FriendlyStatus", "=", "Awaiting Production"],
                  "or",
                  ["FriendlyStatus", "=", "In Production"],
                  "or",
                  ["FriendlyStatus", "=", "Review"],
                ],
                // select: [
                //   "Code",
                //   "FriendlyStatus",
                //   "BranchCode",
                //   "Status",
                //   "WorkOrderCodeSearchText",
                // ],
                sort: "Code desc",
              }
            ),
            displayExpr: "Code",
            valueExpr: "Code",
            searchTimeout: 10,
            searchEnabled: true,
            searchExpr: [
              "WorkOrderCodeSearchText",
              "ProductCode",
              "ProductDescription",
            ],
            // searchMode: "equals",
            dropDownOptions: {
              width: 300,
            },
            readonly: !this.IsWorkOrderEditable,
            // valueChangeEvent: "focusout",
            onFocusIn: (e) => {
              e.event.target.select();
            },
            onItemClick: (e, lineData) => {
              if (e.event)
                this.focusOnEditableCell(lineData.rowIndex, "ProductCode");
            },
            onKeyDown: (e, lineData) => {
              if (backwardTabbing(e) && lineData.rowIndex === 0) {
                e.event.preventDefault();
                this.setFocusOnHeaderForm();
              }
            },
            onValueChanged: (e, lineData) => {
              this.onWorkOrderValueChanged(e, lineData);
            },
            itemTemplate: (data) => {
              return ` <div class="custom-auto-complete">
                        <span class="auto-complete-primary">
                          ${data.Code}
                        </span>
                        <span class="record-secondary gc-grid-code">
                          ${
                            data.ProductCode
                          } - ${data.ProductDescription.trim()}
                        </span>
                      </div>`;
            },
            onOpened: (e, lineData) => {
              const data = lineData.data;
              if (e && !data.WorkOrderCode) {
                e.component.instance().reset();
                e.component.getDataSource().reload();
              }
            },
          },
          width: ColumnWidth.WorkOrder.width,
          maxWidth: ColumnWidth.WorkOrder.minWidth,
          allowEditing: this.IsWorkOrderEditable,
          setCellValue: async (newData, value) => {
            newData.WorkOrderCode = value;

            return newData;
          },
        },
        {
          caption: "Product",
          dataField: "ProductCode",
          cssClass: "edit-cell-template-field",
          allowEditing: true,
          width: ColumnWidth.ProductCode.width,
          maxWidth: ColumnWidth.ProductCode.minWidth,
          editorType: "dxSelectBox",
          editorOptions: {
            valueExpr: "ProductCode",
            displayExpr: "ProductCode",
            searchTimeout: 10,
            searchEnabled: true,
            searchExpr: ["ProductCode", "ProductDescription"],
            onFocusIn: (e, lineData) => {
              const selectBox = e.component;
              selectBox.option(
                "dataSource",
                this.getProductDataSource(lineData.data.WorkOrderCode)
              );
              e.event.target.select();
            },
            itemTemplate: (data) => {
              return ` <div class="custom-auto-complete">
                        <span class="auto-complete-primary">
                          ${data.ProductCode}
                        </span>
                        <span class="record-secondary gc-grid-code">
                          ${data.ProductDescription}
                        </span>
                      </div>`;
            },
            dropDownOptions: {
              width: 300,
            },
            onValueChanged: (e, lineData) => {
              this.onProductValueChanged(e, lineData);
            },
            onItemClick: (e, lineData) => {
              if (e.event) {
                setTimeout(() => {
                  this.focusOnEditableCell(lineData.rowIndex, "ProductUM");
                }, 10);
              }
            },
            onKeyDown: (e, lineData) => {
              if (
                backwardTabbing(e) &&
                !this.IsWorkOrderEditable &&
                lineData.rowIndex === 0
              ) {
                e.event.preventDefault();
                this.setFocusOnHeaderForm();
              }
            },
          },
          visible: this.popupMode === "create",
        },
        {
          caption: "Product",
          dataField: "ProductCode",
          // width: ColumnWidth.Description.width,
          minWidth: ColumnWidth.Description.minWidth,
          cellTemplate: (data, info) => {
            return (data.innerHTML = `<div class="custom-auto-complete">
                        <span class="auto-complete-primary">
                          ${info.data.ProductCode}
                        </span>
                      <span class="record-secondary gc-grid-code">
                        ${info.data.ProductDescription}
                      </span>
                    </div>`);
          },
          visible: this.popupMode !== "create",
        },
        {
          caption: "UM",
          dataField: this.popupMode === "create" ? "ProductUM" : "ProductionUM",
          cssClass: "edit-cell-template-field",
          allowEditing: true,
          width: ColumnWidth.UnitOfMeasure.width,
          maxWidth: ColumnWidth.UnitOfMeasure.minWidth,
          editorType: "dxSelectBox",
          editorOptions: {
            valueExpr: "UnitOfMeasureCode",
            displayExpr: "UnitOfMeasureCode",
            searchTimeout: 10,
            searchEnabled: true,
            searchExpr: ["UnitOfMeasureCode"],
            onFocusIn: (e, lineData) => {
              const selectBox = e.component;
              selectBox.option("dataSource", this.getUMDataSource(lineData));
              e.event.target.select();
            },
            onValueChanged: (e, lineData) => {
              this.onUMValueChanged(e, lineData);
            },
            onItemClick: (e, lineData) => {
              if (e.event) {
                setTimeout(() => {
                  this.onUMForwardTabbing(e, lineData);
                }, 10);
              }
            },
            onKeyDown: (e, lineData) => {
              if (forwardTabbing(e) || onEnter(e)) {
                this.onUMForwardTabbing(e, lineData);
              }
            },
          },
        },
        {
          caption: "Conversion",
          dataField: "Conversion",
          editorType: "dxNumberBox",
          cssClass: "edit-cell-textbox-field",
          format: "##,###,###,###,##0.0000",
          editorOptions: {
            format: "##,###,###,###,##0.0000",
          },
          allowEditing: false,
          width: ColumnWidth.Price4DecimalPoints.width,
          maxWidth: ColumnWidth.Price4DecimalPoints.minWidth,
        },
        {
          caption: "Batch",
          dataField: "BatchNo",
          cssClass: "edit-cell-textbox-field",
          allowEditing: true,
          width: ColumnWidth.BatchNo.width,
          maxWidth: ColumnWidth.BatchNo.minWidth,
          editorOptions: {
            onFocusIn: (e) => {
              e.event.target.select();
            },
            onFocusOut: async (e, lineData) => {
              if (e.event) {
                if (!e.component.option("value")) {
                  await this.displayLineCellError(
                    "Batch is required",
                    "Validation Error"
                  );
                  this.focusOnEditableCell(
                    lineData.rowIndex,
                    lineData.column.dataField
                  );
                }
              }
            },
            onKeyDown: (e, lineData) => {
              if (backwardTabbing(e)) {
                e.event.preventDefault();
                this.focusOnEditableCell(lineData.rowIndex, "ProductUM");
              } else if (forwardTabbing(e) || onEnter(e)) {
                e.event.preventDefault();

                if (
                  this.WorkOrderInstance !== "Core" &&
                  lineData.data.ProductType !== "FG"
                ) {
                  setTimeout(() => {
                    this.focusOnEditableCell(lineData.rowIndex, "CardNo");
                  }, 10);
                } else if (this.ProductionReleasePalletVisibility === "Y") {
                  e.component.blur();

                  setTimeout(() => {
                    this.dataGrid.cellValue(
                      lineData.rowIndex,
                      lineData.column.dataField,
                      e.component.option("value")
                    );

                    this.$nextTick(() => {
                      this.focusOnEditableCell(lineData.rowIndex, "PalletNo");
                    });
                  }, 1);
                } else {
                  this.focusOnEditableCell(
                    lineData.rowIndex,
                    "ProductionUMQty"
                  );
                }
              }
            },
            maxLength: 20,
          },
          setCellValue: async (newData, value, currentRowData) => {
            newData.BatchNo = value;

            this.runNextPalletNo({ ...currentRowData, ...newData });

            return newData;
          },
        },

        {
          caption: "Card No",
          dataField: "CardNo",
          editorType: "dxNumberBox",
          cssClass: "edit-cell-numberbox-field",
          allowEditing: true,
          width: ColumnWidth.CardNo.width,
          maxWidth: ColumnWidth.CardNo.minWidth,
          visible: this.WorkOrderInstance !== "Core",
          format: "#########",
          editorOptions: {
            format: "#########",
            onFocusIn: (e) => {
              e.event.target.select();
            },
            onKeyDown: (e, lineData) => {
              if (forwardTabbing(e) || onEnter(e)) {
                if (this.ProductionReleasePalletVisibility === "Y") {
                  this.$nextTick(() => {
                    const rowIndex = this.dataGrid.getRowIndexByKey(
                      lineData.data.ID
                    );
                    setTimeout(() => {
                      this.focusOnEditableCell(rowIndex, "PalletNo");
                    }, 100);
                  });
                } else if (
                  lineData.data.IsBatchProduct &&
                  lineData.data.IsBatchProduct.toLowerCase() !== "manual"
                ) {
                  e.event.preventDefault();
                  this.focusOnEditableCell(
                    lineData.rowIndex,
                    "ProductionUMQty"
                  );
                }
              } else if (backwardTabbing(e)) {
                if (lineData.data.IsBatchProduct.toLowerCase() !== "manual") {
                  e.event.preventDefault();
                  this.focusOnEditableCell(lineData.rowIndex, "ProductUM");
                }
              }
            },
            maxLength: 20,
            onInput: (e) => {
              const inputElement = e.event.target;
              if (inputElement.value.length > 10)
                inputElement.value = inputElement.value.slice(0, 10);
            },
          },
          setCellValue: async (newData, value, currentRowData) => {
            if (value > 2147483647) {
              await this.displayLineCellError(
                "Please enter a valid Card Number within the acceptable range.",
                "Validation Error"
              );

              newData.CardNo = currentRowData.CardNo;
              const rowIndex = this.dataGrid.getRowIndexByKey(
                currentRowData.ID
              );
              setTimeout(() => {
                this.focusOnEditableCell(rowIndex, "CardNo");
              }, 100);
            } else {
              const items = await this.releaseDataSource.load({
                filter: [["CardNo", "=", value]],
              });
              if (value != null && items.length > 0) {
                await this.displayLineCellError(
                  `Card number ${value} has already been used in this transaction. Please enter a unique card number for this product.`,
                  "Validation Error"
                );

                newData.CardNo = currentRowData.CardNo;
                const rowIndex = this.dataGrid.getRowIndexByKey(
                  currentRowData.ID
                );
                setTimeout(() => {
                  this.focusOnEditableCell(rowIndex, "CardNo");
                }, 100);
              } else {
                newData.CardNo = value;
              }
            }

            if (value) {
              newData.PalletNo = null;
            }

            return newData;
          },
          dataType: "number",
        },
        {
          caption: "Pallet #",
          dataType: "number",
          dataField: "PalletNo",
          cssClass: "edit-cell-numberbox-field",
          editorType: "dxNumberBox",
          format: "##0",
          editorOptions: {
            format: "##0",
            onFocusIn: (e) => {
              e.event.target.select();
            },
            onFocusOut: async (e, lineData) => {
              if (e.event) {
                if (
                  this.ProductionReleasePalletRequired === "Y" &&
                  !e.component.option("value") &&
                  lineData.data.ProductType !== "WIP" &&
                  !lineData.data.CardNo
                ) {
                  if (lineData.data.IsBatchProduct === "manual") {
                    if (lineData.data.BatchNo) {
                      await this.displayLineCellError(
                        "Pallet # is required",
                        "Validation Error"
                      );
                      this.focusOnEditableCell(
                        lineData.rowIndex,
                        lineData.column.dataField
                      );
                    }
                  } else {
                    await this.displayLineCellError(
                      "Pallet # is required",
                      "Validation Error"
                    );
                    this.focusOnEditableCell(
                      lineData.rowIndex,
                      lineData.column.dataField
                    );
                  }
                }
              }
            },
            // onKeyDown: (e, lineData) => {
            //   if (forwardTabbing(e) || onEnter(e)) {
            //     e.event.preventDefault();
            //     this.focusOnEditableCell(lineData.rowIndex, "ProductionUMQty");
            //   } else if (backwardTabbing(e)) {
            //     if (lineData.data.IsBatchProduct.toLowerCase() !== "manual") {
            //       e.event.preventDefault();
            //       this.focusOnEditableCell(lineData.rowIndex, "ProductUM");
            //     }
            //   }
            // },
            max: 99999,
            onInput: (e) => {
              const inputElement = e.event.target;
              if (inputElement.value.length > 6)
                inputElement.value = inputElement.value.slice(0, 6);
            },
          },
          width: ColumnWidth.Quantity.width,
          maxWidth: ColumnWidth.Quantity.minWidth,
          visible: this.ProductionReleasePalletVisibility === "Y",
          setCellValue: async (newData, value) => {
            newData.PalletNo = value;
            return newData;
          },
        },

        {
          caption: "Production UM Qty",
          dataField: "ProductionUMQty",
          cssClass: "edit-cell-numberbox-field",
          editorType: "dxNumberBox",
          format: "##,###,###,###,##0.0000",
          editorOptions: {
            format: "##,###,###,###,##0.0000",
            onFocusIn: (e) => {
              e.event.target.select();
            },
            onKeyDown: (e, lineData) => {
              if (backwardTabbing(e)) {
                e.event.preventDefault();
                if (this.ProductionReleasePalletVisibility === "Y") {
                  this.focusOnEditableCell(lineData.rowIndex, "PalletNo");
                } else if (
                  this.WorkOrderInstance !== "Core" &&
                  lineData.data.ProductType !== "FG"
                ) {
                  this.focusOnEditableCell(lineData.rowIndex, "CardNo");
                } else {
                  if (lineData.data.IsBatchProduct.toLowerCase() !== "manual") {
                    this.focusOnEditableCell(lineData.rowIndex, "ProductUM");
                  } else {
                    this.focusOnEditableCell(lineData.rowIndex, "BatchNo");
                  }
                }
              }
            },
          },
          width: ColumnWidth.Price4DecimalPoints.width,
          maxWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          allowEditing: true,
          setCellValue: async (newData, value, currentRowData) => {
            if (value < 0 || value === 0) {
              await this.displayLineCellError(
                "Invalid quantity entered. Please enter a positive value",
                "Validation Error"
              );

              const rowIndex = this.dataGrid.getRowIndexByKey(
                currentRowData.ID
              );
              setTimeout(() => {
                this.focusOnEditableCell(rowIndex, "ProductionUMQty");
              }, 100);
              return;
            }

            newData.ProductionUMQty = value;
            newData.StockUMQty = (
              value *
              (currentRowData.Conversion / currentRowData.StockUMConversion)
            ).roundNumber(4);

            this.addNextLineOnProductSelect(currentRowData);

            return newData;
          },
        },
        {
          caption: "Stock UM Qty",
          dataField: "StockUMQty",
          cssClass: "edit-cell-numberbox-field",
          editorType: "dxNumberBox",
          format: "##,###,###,###,##0.0000",
          editorOptions: {
            format: "##,###,###,###,##0.0000",
            onFocusIn: (e) => {
              e.event.target.select();
            },
            onKeyDown: (e, lineData) => {
              if (
                (forwardTabbing(e) || onEnter(e)) &&
                this.WorkOrderInstance !== "Core"
              ) {
                if (lineData.data.ProductType === "FG") {
                  e.event.preventDefault();
                  this.focusOnEditableCell(lineData.rowIndex, "Comment");
                }
              }
            },
          },
          width: ColumnWidth.Price4DecimalPoints.width,
          maxWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          allowEditing: true,
          setCellValue: async (newData, value, currentRowData) => {
            if (value < 0 || value === 0) {
              await this.displayLineCellError(
                "Invalid quantity entered. Please enter a positive value",
                "Validation Error"
              );

              const rowIndex = this.dataGrid.getRowIndexByKey(
                currentRowData.ID
              );
              setTimeout(() => {
                this.focusOnEditableCell(rowIndex, "StockUMQty");
              }, 100);
              return;
            }
            newData.StockUMQty = value;
            newData.ProductionUMQty = (
              value *
              (currentRowData.StockUMConversion / currentRowData.Conversion)
            ).roundNumber(4);
          },
        },
        {
          caption: "Location",
          dataField: "StorageLocationCode",
          cssClass: "edit-cell-textbox-field location-code-field",
          allowEditing: true,
          width: ColumnWidth.Location.width,
          maxWidth: ColumnWidth.Location.minWidth,
          visible: this.WorkOrderInstance !== "Core",
          editorOptions: {
            onFocusIn: (e) => {
              e.event.target.select();
              this.onStorageLocationCodeFocusIn();
            },
            buttons: [
              {
                name: "search",
                location: "after",
                options: {
                  icon: "search",
                  onClick: (e, lineData) => {
                    if (e) {
                      this.onLocationSearchClick(lineData);
                    }
                  },
                  width: 25,
                  height: 25,
                },
              },
            ],
            onValueChanged: this.OnNewLocationValueChanged,
          },
          setCellValue: async (newData, value) => {
            newData.StorageLocationCode = value;

            return newData;
          },
        },
        // {
        //   caption: "",
        //   dataField: "LocationSearch",
        //   allowEditing: false,
        //   cellTemplate: "location-search",
        //   visible: this.WorkOrderInstance !== "Core",
        // },
        {
          caption: "Comment",
          dataField: "Comment",
          width: ColumnWidth.Comment.width,
          maxWidth: ColumnWidth.Comment.minWidth,
          cssClass: "edit-cell-textbox-field comment-text",
          allowEditing: true,
          editorOptions: {
            onFocusIn: (e) => {
              e.event.target.select();
            },
            onKeyDown: (e, lineData) => {
              if (backwardTabbing(e) && this.WorkOrderInstance !== "Core") {
                if (lineData.data.ProductType === "FG") {
                  e.event.preventDefault();
                  this.focusOnEditableCell(lineData.rowIndex, "StockUMQty");
                }
              }
            },
            maxLength: 100,
            maxHeight: 103,
            // height: 30,
            valueChangeEvent: "input",
            onInput: (e) => {
              this.commentFieldHeightUpdate(e);
            },
            onInitialized: (e) => {
              this.commentFieldHeightUpdate(e);
            },
          },
          customizeText: (e) => {
            if (e.value) return e.value.replaceAll("\n", "<br/>\r\n");
            else return "";
          },
          encodeHtml: false,
          cellTemplate: (data, info) => {
            return (data.innerHTML = `<div class='comment-cell-template'>${info.text}</div>`);
          },
        },
        {
          caption: "",
          fixed: true,
          fixedPosition: "right",
          dataField: "",
          type: "buttons",
          buttons: [
            {
              name: "copy",
              icon: "copy",
              onClick: (e) => {
                const activeElement = document.activeElement;
                if (activeElement) {
                  activeElement.blur();
                  this.dataGrid.saveEditData();
                }
                setTimeout(() => {
                  this.addCopyReleaseLine(e);
                }, 100);
              },
            },
            {
              name: "Delete Row",
              icon: "trash",
              onClick: async (e) => {
                const confirmDelete = await this.$confirm(
                  "Are you sure you want to delete this production release entry?",
                  "Delete Entry"
                );
                if (confirmDelete) {
                  this.dataGrid.deleteRow(e.row.rowIndex);
                }
              },
            },
          ],
          visible: this.popupMode === "create",
        },
      ];
    },

    getDataSourceConfig() {
      if (this.popupMode === "create") {
        return this.releaseDataSource;
      } else {
        let url = "api/ProductionReleaseDetailList";
        if (this.WorkOrderInstance !== "Core") {
          url = "api/AlmondcoProductionReleaseDetail";
        }

        return WorkOrderService.dataSourceConfig(url, {
          filter: `ProductionReleaseCode eq ${this.popupConfig.dataModel.ProductionReleaseCode}`,
        });
      }
    },
    getGridHeight() {
      return 570;
    },

    dataGrid() {
      return this.$refs["gc-popup-release-model-grid"].instance;
    },
  },
  watch: {
    getHotKeySave: {
      handler(val) {
        if (val) {
          this.$store.dispatch("hotKey/setHotKeySave", false);
          if (this.popupMode !== "review") {
            const activeElement = document.activeElement;
            if (activeElement) {
              activeElement.blur();
              this.dataGrid.saveEditData();
            }
            setTimeout(() => {
              this.onSaveProductionRelease();
            }, 100);
          }
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss">
.gc-popup-work-order-release-model {
  .dx-datagrid-rowsview {
    .dx-editor-cell .dx-texteditor .dx-placeholder::before {
      padding-left: unset;
      padding-top: unset;
      padding-bottom: unset;
    }

    .edit-cell-template-field {
      .dx-textbox.dx-texteditor.dx-editor-underlined {
        border-bottom: 1px solid rgba(0, 0, 0, 0.42) !important;
      }
    }

    tr td {
      vertical-align: top !important;
    }
  }
  .dx-datagrid
    .dx-datagrid-content
    .dx-datagrid-table
    .dx-row
    .edit-cell-template-field.dx-editor-cell {
    padding-left: 5px !important;
  }

  .dx-datagrid
    .dx-datagrid-content
    .dx-datagrid-table
    .dx-row
    .edit-cell-numberbox-field.dx-editor-cell {
    padding-left: 5px !important;
    padding-right: 5px !important;
  }

  .dx-datagrid
    .dx-datagrid-content
    .dx-datagrid-table
    .dx-row
    .edit-cell-textbox-field.dx-editor-cell {
    padding-left: 5px !important;
    padding-right: 5px !important;
  }

  .location-code-field {
    .dx-texteditor-buttons-container {
      background-color: #03a9f4;

      .dx-icon-search::before {
        color: #fff !important;
      }
    }

    .dx-button.dx-button-has-icon:not(.dx-button-has-text):not(
        .dx-shape-standard
      ) {
      border-radius: unset !important;
    }
  }

  .dx-row.dx-data-row {
    td.required-field {
      // background-color: #ff00003b !important;
      .dx-texteditor:not(.dx-state-readonly)::after {
        content: "";
        border-bottom: 2px solid rgb(255 0 0) !important;
      }
    }
  }
}
</style>
